import React, { useRef, useState, useEffect } from 'react'
import DxDataTable from 'components/DxDataTable'
import { Template } from 'devextreme-react/core/template'
import { Editing } from 'devextreme-react/data-grid'
import { Button as DxButton } from 'devextreme-react/button'
import readXlsxFile from 'read-excel-file'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import createXLS from 'helpers/fileGenerators/xlsGenerator'

const StyledDxDataTable = styled(DxDataTable)`
	max-height: calc(100vh - 144px);
	.dx-focused {
		overflow: visible !important;
	}
	tr {
		height: 36px;
	}
	td {
		padding: 0 7px !important;
	}
`

const GridView = ({ sectionPath, onImported, selectedRows, exportData, onMultiEditEnabled, ...props }) => {
	const history = useHistory()
	const importedData = useRef()
	const lastAddInd = useRef(0)
	const tableRef = useRef()

	const handleUpload = e => {
		tableRef.current.instance.beginCustomLoading()
		const file = e.target.files[0]
		readXlsxFile(file)
			.then(data => {
				if(Array.isArray(data)) {
					const colsMap = props?.columns?.reduce?.((outObj, item) => ({
						...outObj,
						[item.caption.toLowerCase()]: item.dataField
					}), {})
					const [fileCols, ...rows] = data
					if(Array.isArray(fileCols) && Array.isArray(rows)) {
						const tableData = rows.reduce((outArr, row) => {
							const parsedRow = {}
							row.forEach((item, i) => {
								const colName = fileCols?.[i]?.trim()?.toLowerCase()
								const caption = colsMap[colName]
								parsedRow[caption] = item
							})
							outArr.push(parsedRow)
							return outArr
						}, [])
						const dataGridInst = tableRef?.current?.instance
						importedData.current = tableData
						tableData.reverse().forEach(row => {
							dataGridInst.addRow()
						})
						if(typeof onImported === 'function') {
							onImported()
						}
					}
				}
			})
		tableRef.current.instance.endCustomLoading()
	}

	const [selectedData, setSelectedData] = useState([])
	const [isMultiAddEnabled, setIsMultiAddEnabled] = useState(false)

	useEffect(() => {
		if (selectedRows && selectedRows.length) {
			setSelectedData(props.data.filter(item => selectedRows.includes(item.id)))
		}
	}, [props.data, selectedRows])

	const multiAddRender = () => {
		return(
			<DxButton
				type={ isMultiAddEnabled ? 'default' : 'normal' }
				icon='rename'
				onClick={() => {
					if (!isMultiAddEnabled) {
						tableRef.current.instance.addRow()
					}
					setIsMultiAddEnabled(prevValue => {
						onMultiEditEnabled(!prevValue)
						return !prevValue
					})
				}}
			/>
		)
	}

	const importRender = () => {
		return(
			<label>
				<input hidden type='file' onChange={handleUpload} accept='.xlsx' multiple={false} />
				<DxButton icon='inserttable' />
			</label>
		)
	}

	const exportRender = () => {
		return(
			<DxButton icon='xlsxfile' onClick={exportXLSX} disabled={!selectedData.length} />
		)
	}

	const exportXLSX = () => {
		createXLS({
			...exportData,
			selectedData
		})
	}

	// Fired during import on creating new row
	const handleAdd = e => {
		if(importedData.current) {
			e.data = importedData.current[lastAddInd.current]
			lastAddInd.current++ // eslint-disable-line no-plusplus
			if(lastAddInd.current === importedData.current?.length) {
				importedData.current = null
			}
		}
	}
	const MultiAddTemplate = <Template key='multiAdd' name='multiAdd' render={multiAddRender} />
	const ImportTemplate = <Template key='import' name='import' render={importRender} />
	const ExportTemplate = <Template key='export' name='export' render={exportRender} />

	return(
		<StyledDxDataTable
			{...props}
			getRef={ref => {
				tableRef.current = ref
			}}
			withSearch
			columnAutoWidth={false}
			remoteOperations={true}
			repaintChangesOnly={true}
			onKeyDown={async ({ event, ...rest }) => {
				if(event.target.id.includes('react-select') && (
					event.key === 'ArrowUp' ||
					event.key === 'ArrowDown' ||
					event.key === 'Enter'
				)) {
					event.preventDefault()
				}
				if(isMultiAddEnabled && event.key === 'Enter' && event.ctrlKey) {
					event.preventDefault()
					await tableRef.current.instance.saveEditData()
					tableRef.current.instance.addRow()
				}
			}}
			onToolbarPreparing={e => {
				e.toolbarOptions.items.push({
					widget: 'dxButton',
					location: 'after',
					options: {
						icon: 'arrowleft',
						onClick: () => history.push(sectionPath)
					}
				})
				onMultiEditEnabled && e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'multiAdd'
				})
				e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'import'
				})
				selectedRows && e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'export'
				})
			}}
			onInitNewRow={handleAdd}
			templates={[MultiAddTemplate, ImportTemplate, ExportTemplate]}
		>
			<Editing
				mode='batch'
				allowUpdating={true}
				allowAdding={true}
			/>
		</StyledDxDataTable>
	)
}

export default GridView
